import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import { Logger } from 'fsts';
import { namespace } from 'vuex-class';
import admpNew, { AdmpNews } from '@/shared/model/admpNews';
import D4yEdit from '@/components/d4y-edit/d4y-edit.vue';
import { CONST } from '@/shared/utils/Constants';
import { ROUTES } from '@/router/routesEnum';
import DateUtils from '@/shared/utils/DateUtils';
import DateField from '@/components/_common/date-field/DateField.vue';
import { Contact } from '@/shared/model/contact';
import GeneralUtils from '@/shared/utils/generalUtils';
import DocumentUpload from '@/components/_common/document-upload/document-upload.vue';
import AdmpNewsDocuments from './admp-news-documents/admp-news-documents.vue';
import { DefaultViewHelper } from '@/views/ViewHelper';
import { idMask } from '@/components//idMaskService';

const name = 'admp-news';
const logger = new Logger(name);
const authModule = namespace('auth');
const admpNewsModule = namespace('admpNews');
const contactModule = namespace('contact');

const delay = GeneralUtils.delay; // delay function

@Component({
  name: name,
  components: {
    D4yEdit,
    DateField,
    DocumentUpload,
    AdmpNewsDocuments,
  },
})
export default class AdmpNewsView extends Vue {
  @Ref('document-upload-cmp-admp-news')
  private documentUploadCmpRef!: any;
  @Ref('observer-add-edit-admp-news-form')
  private addEditAdmpNewsForm!: any;
  @Ref('admpListNewsDocuments')
  private admpListNewsDocumentsRef!: any;
  @Ref('observer-add-edit-admp-news-document-form')
  private admpNewsObserverForm!: any;
  @Ref('imageFileInput')
  private imageFileInput!: HTMLInputElement;

  @admpNewsModule.Action('uploadNewsDocument')
  private actionUploadNewsDocument!: any;
  @admpNewsModule.Action('uploadNewsImage')
  private actionUploadNewsImage!: any;
  @admpNewsModule.Action('getNewsFileDownloadLink')
  private actionGetNewsFileDownloadLink!: any;

  @authModule.Getter('isUserSupport')
  private isUserSupport!: any;
  @authModule.Getter('isUserGsUser')
  private isUserGsUser!: any;
  @authModule.Getter('isUserOnlyGs')
  private isUserOnlyGs!: any;
  @authModule.Getter('isUserSupportOrSuperAdmin')
  private isUserSupportOrSuperAdmin!: any;

  @authModule.Getter('contactType')
  private contactType!: any;
  @authModule.Getter('zrNummer')
  private zrNummer!: any;

  @admpNewsModule.Action('getAdmpNews')
  private actionGetAdmpNews!: any;

  @admpNewsModule.Action('updateAdmpNews')
  private actionUpdateAdmpNews!: any;
  @admpNewsModule.Action('upsertAdmpNewsReadInfo')
  private actionUpsertAdmpNewsReadInfo!: any;
  @admpNewsModule.Action('getNewsCountForUser')
  private actionGetNewsCountForUser: any;
  @admpNewsModule.Getter('getAdmpNewsContactType')
  private admpNewsContactType: any;
  @contactModule.Getter('getContactForLoggedInUser')
  private contactForLoggedInUser!: Contact;
  @admpNewsModule.Action('deleteAdmpNewsFile')
  private actionDeleteAdmpNewsFile!: any;
  @admpNewsModule.Action('updateAdmpNewsImages')
  private actionUpdateAdmpNewsImages!: any;

  private news: Array<any> = [];

  get admpNewsFilesArr() {
    return this.admpNews.news_files.filter((x: any) => x.bucket_name == 'admpnl-news-anlagen');
  }

  private isRecordLoaded = false;
  private isPdfLoaded = false;
  private isShowBackupImage = false;
  public isImageUploading: boolean = false;
  public isDeleteImage: boolean = false;
  public isDeleteImageDialogOpen: boolean = false;
  public showSuccessSnackbar: boolean = false;
  public snackbarMessage: string = '';
  public snackbarTitle: string = '';

  get admpNewsId() {
    if (this.admpNewsCreatedId && this.admpNewsCreatedId !== 0) {
      return String(this.admpNewsCreatedId);
    }
    if (this.admpNews && this.admpNews.id && this.admpNews.id !== 0) {
      return String(this.admpNews.id);
    }

    const guid = this.$route.params['admpNewsId'];
    if (guid && guid !== '0' && guid !== '0000000000000000' && guid !== CONST.emptyGuid) {
      const realId = idMask.decode(guid);
      if (realId !== null && realId !== 0) {
        return String(realId);
      }
    }
    if (this.admpNewsCreatedId && this.admpNewsCreatedId !== 0) {
      return String(this.admpNewsCreatedId);
    }
    return '0000000000000000';
  }

  get isHouseAdmin() {
    return this.contactForLoggedInUser.isHouseAdmin;
  }

  get isNewsCreate() {
    return this.admpNewsId === '0' || this.admpNewsId === '0000000000000000' || !this.admpNewsId;
  }

  @Watch('$i18n.locale')
  private onLocaleChanged() {
    document.title = this.$i18n.tc('news_tab_form');
  }

  @Watch('zrNummer')
  onZrNummerChange(newVal: any) {
    if (newVal && !this.isNewsCreate && !this.isRecordLoaded) {
      this.getAdmpNews();
    }
  }

  async created() {
    document.title = this.$i18n.tc('news_tab_form');
    if (!this.isUserSupportOrSuperAdmin && !this.isUserGsUser) {
      this.goToNewsList();
    }

    if (!this.isNewsCreate) {
      await this.getAdmpNews();
    } else if (this.isNewsCreate) {
      this.isRecordLoaded = true;
    }
  }

  // when change record via `next/previous` button the `mounted` method will NOT be called
  async mounted() {
    if (this.admpNewsId == '0') {
      const result = await this.addEditAdmpNewsForm.validate();
    }
  }

  showBackupImage(documentType: string) {
    this.isPdfLoaded = true; // ignore PDF loading
  }

  async getAdmpNews(showSkeleton: boolean = true) {
    if (this.isNewsCreate) {
      return;
    }

    if (showSkeleton) {
      this.isRecordLoaded = false; // change loading flag when change `admpNews` via `next/previous` buttons
    }

    const payload = {
      id: this.admpNewsId,
      contactType: this.contactType?.toLowerCase(),
    };

    await this.actionGetAdmpNews(payload)
      .then(async (response: any) => {
        if (!response || !response?.id) {
          console.warn('You have no rights to see this News');
          DefaultViewHelper.showSnackbarError(this.$i18n.tc('error.invalid_link_redirect'), 4000);
          await delay(500);
          this.goToNewsList();
          return;
        }

        const currentImage = this.admpNews.news_image;

        console.log('zre Document response :>> ', response);
        this.admpNews = Object.assign({}, admpNew.parse(response));
        this.isRecordLoaded = true; // show form earlier (after initial `skeleton`)
        let image = this.admpNews.news_files.find((x: any) => x.bucket_name == 'admpnl-news-images');
        console.log('image :>> ', image);
        if (image) {
          if (currentImage) {
            this.admpNews.news_image = currentImage;
            this.imgParams.src = currentImage;
          }
          if (!this.admpNews.news_image) {
            let imagePayload = {
              FileName: image.filename,
              BucketName: image.bucket_name,
              DisplayName: image.display_name,
            };
            await this.actionGetNewsFileDownloadLink(imagePayload)
              .then((res: any) => {
                console.log('actionGetNewsFileDownloadLink image res :>> ', res);
                this.admpNews.news_image = res;
                this.imgParams.src = res;
              })
              .finally(() => {
                if (this.isUserOnlyGs && this.isUnread) {
                  this.markAsRead();
                }
              });
          }
        } else {
          if (this.isUserOnlyGs && this.isUnread) {
            this.markAsRead();
          }
        }
      })
      .catch((err: any) => {
        logger.error(err);
      })
      .finally(() => {
        this.isRecordLoaded = true;
      });
  }

  private admpNews: AdmpNews = admpNew.parse({});

  isLoading = false;
  titleInfo = 'empty';

  get image() {
    return {
      src: 'gs_details.svg',
      header: '',
      width: 230,
      height: 210,
    };
  }

  imgParams = {
    src: 'belege_stub_big.png',
    height: this.dynamicImageSize.height,
    width: this.dynamicImageSize.width,
  };
  get newsImg() {
    return this.imgParams;
  }

  get allowFalseForCheckboxes() {
    return this.admpNews.is_valid_for_haus_admin || this.admpNews.is_valid_for_intern;
  }

  @Watch('$vuetify.breakpoint.width')
  public async onOptionsFilterChanged(newVal: any, oldVal: any) {
    // console.log('newVal :>> ', newVal);
    // console.log('oldVal :>> ', oldVal);
    this.recreateImage();
  }

  /**
   * xs < 600px, sm 600-960px, md 960 - 1264
   */
  get textAreaHeight() {
    let resultHeight = 500;
    if (this.$vuetify.breakpoint.xlOnly) {
      resultHeight = 595;
    } else if (this.$vuetify.breakpoint.lgOnly) {
      resultHeight = 555;
    } else if (this.$vuetify.breakpoint.mdOnly) {
      resultHeight = 555;
    }
    return resultHeight;
  }

  get dynamicImageSize() {
    // if (this.$vuetify.breakpoint.xlOnly) {
    //   return { height: 610, width: 450 };
    // } else if (this.$vuetify.breakpoint.lgOnly && this.$vuetify.breakpoint.width > 1580) {
    //   return { height: 510, width: 350 };
    // } else if (this.$vuetify.breakpoint.lgOnly && this.$vuetify.breakpoint.width < 1580) {
    //   return { height: 450, width: 300 };
    // } else if (this.$vuetify.breakpoint.mdOnly) {
    //   return { height: 410, width: 250 };
    // }
    let result = { height: 265, width: 320 };
    if (this.$vuetify.breakpoint.xlOnly) {
      result = { height: 305, width: 340 };
    } else if (this.$vuetify.breakpoint.width < 1440 && this.$vuetify.breakpoint.width >= 1264) {
      result = { height: 265, width: 300 };
    } else if (this.$vuetify.breakpoint.width < 1264 && this.$vuetify.breakpoint.width > 1100) {
      // console.log('100000 :>> ');
      result = { height: 265, width: 300 };
    } else if (this.$vuetify.breakpoint.width < 1100) {
      // console.log('100000 :>> ');
      result = { height: 265, width: 230 };
    }

    return result;
    // return { height: 515, width: 350 };
  }

  recreateImage() {
    let x = this.dynamicImageSize;
    // console.log('x.height :>> ', x.height);
    // console.log('this.imgParams.height :>> ', this.imgParams.height);
    // let obj = {...this.imgParams, ...x}
    if (x.height != this.imgParams.height) {
      let obj = { ...this.imgParams, ...x };
      this.imgParams = obj;
    }
  }

  get noticeHeaders() {
    let headers: {
      text: string | any;
      value: string;
      sortable?: boolean;
      width?: string;
      align?: string;
    }[] = [
      { text: this.$t('busy_type'), value: 'belegTyp', sortable: false, width: '209' },
      { text: this.$t('date'), value: 'datumInvoice', sortable: false, align: 'center' },
      { text: this.$t('gross_amount'), value: 'bruttobetrag', sortable: false, align: 'right' },
    ];
    return headers;
  }

  //#region Logic related to `status` dialog

  updateNewsFormData(news: any) {
    logger.log('result updateNewsFormData (after `status dialog` update):>> ', news);
    const responseDocument = document;
    // this.zrDocument.bonusgutschrift = document.bonusgutschrift;
    // this.zrDocument.skontierfaehig = document.skontierfaehig;
    // this.zrDocument.faelligkeit = document.faelligkeit;

    // this.zrDocument.status_Freigabe = responseDocument.status_Freigabe;
    // this.zrDocument.bearbeit_Am = responseDocument.bearbeit_Am;
    // this.zrDocument.bearbeit_Von = responseDocument.bearbeit_Von;
    // if (!this.zrDocument.kommission_Nummer) {
    //   this.zrDocument.kommission_Nummer = responseDocument.kommission_Nummer;
    // }

    // this.zrDocument.text_nichtbuchen = responseDocument.text_nichtbuchen;
  }

  showMenu = false;
  closeMenu() {
    this.showMenu = false;
  }

  //#endregion

  changeValidFromDate(value: any) {
    this.admpNews.valid_from = value || undefined;
  }

  changeValidUntilDate(value: any) {
    this.admpNews.valit_until = value || undefined;
  }

  private navigateToNewCreatedRecord(id: any) {
    const hashId = idMask.encode(id);
    this.$router.push({
      name: ROUTES.newsaddedit,
      params: { admpNewsId: hashId + '' },
    });
  }

  private admpNewsCreatedId: number = 0;
  async onClickedUpdate() {
    const result = await this.addEditAdmpNewsForm.validate();
    if (result) {
      this.isLoading = true;

      this.admpNews.id = +this.admpNewsId;
      if (this.admpNews.id == 0) {
        this.admpNews.created_by = this.contactForLoggedInUser?.firstName + ' ' + this.contactForLoggedInUser?.lastName; //this.contactFullName;
      }
      await this.actionUpdateAdmpNews(this.admpNews)
        .then((response: any) => {
          console.log('response :>> ', response);
          this.admpNewsCreatedId = response.result.id;
          if (this.isNewsCreate) {
            this.imageUploadCmpRef.upload();

            if (
              (this.imageUploadCmpRef.document == null &&
                this.documentUploadCmpRef == null &&
                this.dialogFstDocRef == null) ||
              (this.imageUploadCmpRef.document != null &&
                this.documentUploadCmpRef == null &&
                this.dialogFstDocRef == null)
            ) {
              this.navigateToNewCreatedRecord(this.admpNewsCreatedId);
            } else if (
              this.imageUploadCmpRef.document == null &&
              (this.documentUploadCmpRef?.document != null || this.dialogFstDocRef != null)
            ) {
              setTimeout(() => {
                this.dialogFstDocRef.upload();
              }, 1000);
            }
          }

          this.snackbarTitle = this.$te('success') ? (this.$t('success') as string) : 'Erfolgreich gespeichert';
          this.snackbarMessage = this.$te('success_created')
            ? (this.$t('success_created') as string)
            : 'News wurde erfolgreich gespeichert';
          this.showSuccessSnackbar = true;

          this.actionGetNewsCountForUser({
            contactRecordID: this.contactForLoggedInUser?.recordID,
            isUserInAzureAdminGroup: this.isUserSupportOrSuperAdmin,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }

  private dialogFstDocRef: any = null;
  updateDocumentOnCreate(documentRef: any, fileName: string) {
    this.dialogFstDocRef = documentRef;
    this.attachmentFileName = fileName;
  }

  get isUnread() {
    return (
      this.admpNews.news_read_info.length == 0 ||
      (this.admpNews.news_read_info.length == 1 && this.admpNews.news_read_info.every((x: any) => !x.is_read))
    );
  }

  markAsUnRead() {
    logger.log('markAsUnRead');
    this.changeReadStatus(false);
  }

  isMarkingRead = false;
  markAsRead() {
    logger.log('markAsRead');
    this.changeReadStatus(true);
  }

  changeReadStatus(is_read: boolean) {
    let payload: any = {
      zr_news_id: this.admpNews.id,
      is_read: is_read,
      user_id: '',
      UpdateRead: true,
    };
    if (this.admpNews.news_read_info.length == 1) {
      payload.id = this.admpNews.news_read_info[0].id;
    }

    this.isMarkingRead = true;
    this.actionUpsertAdmpNewsReadInfo(payload)
      .then((result: any) => {
        console.log('actionUpsertAdmpNewsReadInfo resultresult:>> ', result);
        this.admpNews.news_read_info.splice(0, 1, result.result);
      })
      .catch((err: any) => {
        logger.error(err);
      })
      .finally(() => {
        this.isMarkingRead = false;
      });
  }

  goToNewsList() {
    this.$router.push({
      name: ROUTES.newslist,
    });
  }

  //#region Next/previous logic
  get currentIdPosition() {
    return this.news.findIndex((x: any) => x.id == this.admpNewsId);
  }

  get isFirstNews() {
    return this.currentIdPosition == 0;
  }

  get isLastNews() {
    return this.currentIdPosition == this.news.length - 1;
  }

  goToPrev() {
    const prevId = this.news[this.currentIdPosition - 1].id;

    this.$router.push({
      name: ROUTES.newsaddedit,
      params: { admpNewsId: prevId },
    });
    this.getAdmpNews();
  }

  goToNext() {
    const nextId = this.news[this.currentIdPosition + 1].id;

    this.$router.push({
      name: ROUTES.newsaddedit,
      params: { admpNewsId: nextId },
    });
    this.getAdmpNews();
  }
  //#endregion

  getTitle(value: string) {
    if (value != null && value != '-') {
      // return this.$t('edited_by') + ':  ' + value + ', ';
      return value + ', ';
    } else {
      return '';
    }
  }

  submitForm(e: any) {
    e.preventDefault(); // (GSP-076) prevent reload page when click on any button in PDF preview (vue-pdf-app) toolbar
  }

  //#region Logic to upload image
  @Ref('image-upload-cmp')
  private imageUploadCmpRef!: any;
  @Ref('AdmpNewsDocuments')
  private AdmpNewsDocuments!: any;

  isLoadingImage: boolean = false;
  private isImageUploaded = false;
  private imageData: any = {}; // Document = { ...DocumentEmpty };
  private imageModel!: any; //Document

  get hasUploadedImage() {
    return this.isImageUploaded;
  }

  async uploadImage(payload: any) {
    logger.debug('uploadImage');
    // payload.id = this.model.id;
    // payload.parentId = this.parentId;
    this.isLoadingImage = true;
    payload.FileName = payload.document.name;
    payload.BucketName = 'admpnl-news-images';
    payload.AdmpNewsId = this.admpNewsCreatedId || this.admpNews.id;
    await this.actionUploadNewsDocument(payload)
      .then((result: any) => {
        logger.debug('uploadImage result :>> ', result);
        if (result.isValid) {
          // this.model.file_id = result.id;
          // this.setDocumentFileInfo(result.result); // `result.result` contains DocumentInfo (Name, Extension, Size) from successfully uploaded file on the Backend
          this.isImageUploaded = true;
          this.admpNews.news_image = this.onImageUploadUrl;
          this.imgParams.src = this.onImageUploadUrl;
          // Vue.set(this.imgParams,'src', result.result.minioLink);
          // this.$emit('document:uploaded'); // need to load list documents (not our case)
          this.$emit('document:getData', this.documentData); // better emit result of Document creation + File Upload here than make extra request from `*-documents` table
          // also Important to get the last changes when open dialog, if document was changed by someone else
        }
      })
      .finally(() => {
        if (this.isNewsCreate && this.dialogFstDocRef != null) {
          // this.documentUploadCmpRef?.document != null
          this.dialogFstDocRef.upload();
        }
        this.isLoadingImage = false;
        // this.resetDocumentData();
        // this.onClickedClose();
      });
  }

  private onImageUploadUrl = '';
  private async fileInputImageChange(isInputHasFile: boolean, fileInfo: any) {
    const blob = new Blob([fileInfo], { type: 'image/png' });
    this.onImageUploadUrl = URL.createObjectURL(fileInfo);
    // this.imgParams.src = URL.createObjectURL(event!.target!.files[0])

    if (isInputHasFile && fileInfo && fileInfo.size <= CONST.maxFileSize5MB) {
      logger.debug('fileInputImageChange');
      // this.setDocumentInfo();
      if (!this.isNewsCreate) {
        this.imageUploadCmpRef.upload();
      }
      return;
      await this.changeVoucherDocumentAddEditDialogOnUpdate()
        .then(async (result: any) => {
          logger.debug('onSave result:>> ', result);
          // this.model.id = result.id;
          // this.documentData = { ...result.result };
          // this.uploadDocumentBtnClick();
        })
        .catch((err: any) => {});
      // await this.actionUpdateDocument(this.documentData)
      //   .then(async (result: any) => {
      //     logger.debug('onSave result:>> ', result);
      //     this.model.id = result.id;
      //     this.documentData = { ...result.result };
      //     this.uploadDocumentBtnClick();
      //   })
      //   .catch((err: any) => {});
    }
  }

  //#endregion

  async downloadFile(e: any) {
    let attachmentFile = this.admpNews.news_files.find((x: any) => x.bucket_name == 'admpnl-news-anlagen');
    // console.log('image :>> ', attachmentFile);
    if (attachmentFile) {
      let attachmentPayload = {
        FileName: attachmentFile.filename,
        BucketName: attachmentFile.bucket_name,
        DisplayName: attachmentFile.display_name,
        OriginalName: attachmentFile.original_name,
        IsSaveOnDisk: true,
      };
      await this.actionGetNewsFileDownloadLink(attachmentPayload).then((res: any) => {});
    }
  }

  //#region Logic to upload document
  isLoadingDocument: boolean = false;
  private isDocUploaded = false;
  private documentData: any = {}; // Document = { ...DocumentEmpty };
  private model!: any; //Document

  get fileName() {
    const attachment = this.admpNews.news_files.find((x: any) => x.bucket_name == 'admpnl-news-anlagen');
    return attachment ? attachment.display_name : '';
  }

  private documentFileInfo: {
    documentName: string;
    documentType: string;
    documentSize: string;
  } = {
    documentName: '',
    documentType: '',
    documentSize: '',
  };
  get hasUploadedDocument() {
    return this.isDocUploaded;
  }

  get uploadedDocumentInfo() {
    if (!this.documentFileInfo.documentName) return '';
    return `${this.documentFileInfo.documentName}.${this.documentFileInfo.documentType} (${(
      +this.documentFileInfo.documentSize / 1000
    ).toFixed(1)} KB)`;
  }

  private setDocumentFileInfo(docFileInfo: any) {
    this.documentFileInfo!.documentName = docFileInfo.documentName;
    this.documentFileInfo!.documentType = docFileInfo.documentType;
    this.documentFileInfo!.documentSize = docFileInfo.documentSize;
    this.documentData = { ...this.documentData, ...this.documentFileInfo }; /// TODO: (LOW) write documentFileInfo in DocumentData (maybe we don't need that and delete in future)
  }

  private attachmentFileName = '';
  private onNameInputChange(value: any) {
    console.log('onNameInputChange value:>> ', value);
    this.attachmentFileName = value;
  }

  async uploadDocument(payload: any) {
    console.log('payload :>> ', payload);
    logger.debug('uploadDocument');
    // payload.id = this.model.id;
    // payload.parentId = this.parentId;
    this.isLoadingDocument = true;
    payload.FileName = this.attachmentFileName;
    payload.BucketName = 'admpnl-news-anlagen';
    payload.admpNewsId = this.admpNewsCreatedId || this.admpNews.id;
    await this.actionUploadNewsDocument(payload)
      .then((result: any) => {
        logger.debug('uploadDocument result :>> ', result);
        if (result.isValid) {
          // this.model.file_id = result.id;
          this.setDocumentFileInfo(result.result); // `result.result` contains DocumentInfo (Name, Extension, Size) from successfully uploaded file on the Backend
          this.isDocUploaded = true;
          this.admpNews.news_attachment = result.result.minioLink;
          this.pushFileInAdmpNewsFiles(result.result);
          // this.$emit('document:uploaded'); // need to load list documents (not our case)
          this.$emit('document:getData', this.documentData); // better emit result of Document creation + File Upload here than make extra request from `*-documents` table
          // also Important to get the last changes when open dialog, if document was changed by someone else
        }
      })
      .finally(() => {
        this.isLoadingDocument = false;
        this.resetDocumentData();
        this.onClickedClose();

        delay(1500).then(() => {
          if (this.isNewsCreate) {
            this.navigateToNewCreatedRecord(this.admpNewsCreatedId);
          }
        });
      });
  }
  private pushFileInAdmpNewsFiles(file: any) {
    let isFilePresent = this.admpNews.news_files.find((x: any) => x.id == file.id);
    if (!isFilePresent) {
      this.admpNews.news_files.push(file);
    }
  }

  private setDocumentInfo() {
    this.documentData!.type = 'pdf'; //this.documentType;
    this.documentData!.for = 'all'; // TODO: (EGRUP-143) temporary solution until find out logic about `FOR` field
    //this.documentData.title = this.model.title;
  }
  //
  //size:5120|required|ext:pdf,jpg
  // size:5120|required|ext:pdf,jpg
  private async fileInputChange(isInputHasFile: boolean, fileInfo: any) {
    if (isInputHasFile && fileInfo && fileInfo.size <= CONST.maxFileSize5MB) {
      logger.debug('fileInputChange');
      this.setDocumentInfo();
      if (!this.isNewsCreate) {
        this.documentUploadCmpRef.upload();
      }
      return;
      await this.changeVoucherDocumentAddEditDialogOnUpdate()
        .then(async (result: any) => {
          logger.debug('onSave result:>> ', result);
          // this.model.id = result.id;
          // this.documentData = { ...result.result };
          // this.uploadDocumentBtnClick();
        })
        .catch((err: any) => {});
      // await this.actionUpdateDocument(this.documentData)
      //   .then(async (result: any) => {
      //     logger.debug('onSave result:>> ', result);
      //     this.model.id = result.id;
      //     this.documentData = { ...result.result };
      //     this.uploadDocumentBtnClick();
      //   })
      //   .catch((err: any) => {});
    }
  }
  changeVoucherDocumentAddEditDialogOnUpdate(): any {
    // throw new Error('Method not implemented.');
    return new Promise((resolve, reject) => {});
  }
  uploadDocumentBtnClick() {
    this.documentUploadCmpRef.upload();
  }
  async downloadDocument() {
    logger.debug('downloadDocument');
    // await this.actionDownloadDocument(this.model!.file_id);
  }
  deleteDocument() {
    logger.debug('deleteDocument');
    // this.actionDeleteDocument(this.model.file_id).then(async (result: any) => {
    //   this.isDocUploaded = false;
    // });
    // this.model.parentId = '';
  }

  onClickedClose() {
    this.resetDocumentData();
    this.resetDocumentFileData();
    // this.observerForm.reset();
    this.$emit('click:close', { updateList: true });
  }

  /**
   * Reset `documentData` after successful upload, otherwise there is Bug when try to upload next document via dialog (BUG: ID saved without reset, so it does NOT create new document but UPDATE existing)
   */
  private resetDocumentData() {
    // this.documentData = {}; //{ ...DocumentEmpty };
  }

  /**
   * Clear `document-upload` Name/size data before closing the dialog to avoid showing wrong data for new dialog
   */
  private resetDocumentFileData() {
    this.documentFileInfo = {
      documentName: '',
      documentType: '',
      documentSize: '',
    };
  }

  async createNewsWithDialog() {
    if (this.admpNews.id == 0) {
      await this.createNews().then((res: any) => {
        (document.querySelector('#image-upload-cmp #document__upload__input__document') as any)?.click();
      });
    } else {
      (document.querySelector('#image-upload-cmp #document__upload__input__document') as any)?.click();
    }
  }

  async createNewsForAdmpNewsDocument(e: any) {
    if (this.admpNews.id == 0 && !this.isFillRequiredFields) {
      await this.createNews().then((res: any) => {
        setTimeout(() => {
          this.admpListNewsDocumentsRef.onAdd();
        }, 1000);
      });
    }
  }

  private async createNews() {
    const result = await this.addEditAdmpNewsForm.validate();
    if (result) {
      this.isLoading = true;

      this.admpNews.id = +this.admpNewsId;
      this.admpNews.created_by = this.contactForLoggedInUser?.firstName + ' ' + this.contactForLoggedInUser?.lastName; //this.contactFullName;
      await this.actionUpdateAdmpNews(this.admpNews)
        .then((response: any) => {
          this.admpNews = response.result;
          this.admpNewsCreatedId = response.result.id;
          this.navigateToNewCreatedRecord(this.admpNewsCreatedId);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }

  get isFillRequiredFields() {
    if (
      this.admpNews.title?.length != 0 &&
      this.admpNews.text?.length != 0 &&
      this.admpNews.valid_from?.length != 0 &&
      (this.admpNews.is_valid_for_intern == true || this.admpNews.is_valid_for_haus_admin == true)
    ) {
      return false;
    } else {
      return true;
    }
  }

  public async handleImageSelectClick() {
    if (!this.isUserSupportOrSuperAdmin) return;
    const isValid = await this.addEditAdmpNewsForm.validate();
    if (!isValid) return;

    // If the entry exists, open the file selection dialogue
    const currentId = this.admpNews.id || this.admpNewsCreatedId;
    if (currentId && currentId > 0 && !this.isNewsCreate) {
      this.imageFileInput?.click();
      return;
    }

    // If a new news item is being created — save it first, then open the selection dialogue
    this.isLoading = true;
    try {
      this.admpNews.id = 0;
      this.admpNews.created_by =
        (this.contactForLoggedInUser?.firstName || '') + ' ' + (this.contactForLoggedInUser?.lastName || '');

      const response = await this.actionUpdateAdmpNews(this.admpNews);
      const createdId = response?.result?.id ?? 0;
      const isSuccess = createdId > 0 || response?.isValid === true || !!response?.result;
      if (isSuccess) {
        this.snackbarTitle = this.$te('success') ? (this.$t('success') as string) : 'Erfolgreich gespeichert';
        this.snackbarMessage = this.$te('success_created')
          ? (this.$t('zr_news.success_created') as string)
          : 'News wurde erfolgreich gespeichert';
        this.showSuccessSnackbar = true;
      }

      if (createdId) {
        this.admpNewsCreatedId = createdId;
        this.admpNews.id = createdId;
        this.admpNews = Object.assign({}, this.admpNews, response.result);

        this.updateUrlSilently(createdId);
        this.$nextTick(() => {
          this.imageFileInput?.click();
        });
      }
    } catch (e) {
      logger.error('Error saving news before image selection:', e);
    } finally {
      this.isLoading = false;
    }
  }

  private updateUrlSilently(id: any) {
    try {
      const hashId = idMask.encode(id);
      const resolved = this.$router.resolve({
        name: ROUTES.newsaddedit,
        params: { admpNewsId: String(hashId) },
      });
      if (resolved && resolved.href) {
        window.history.replaceState(window.history.state, '', resolved.href);
      }
    } catch (e) {
      logger.warn('Could not update URL silently', e);
    }
  }

  /**
   * Handling the "Dokument hinzufügen" click WITHOUT resetting to the skeleton
   */
  public async handleAddDocumentClick() {
    if (!this.isUserSupportOrSuperAdmin) return;

    // 1. Form validation
    const isValid = await this.addEditAdmpNewsForm.validate();
    if (!isValid) {
      return;
    }

    const currentId = this.admpNews.id || this.admpNewsCreatedId;

    // 2A. The news item already exists in the DB — just open the dialogue
    if (currentId && currentId > 0 && !this.isNewsCreate) {
      this.admpListNewsDocumentsRef?.openAddDialog();
      return;
    }

    // 2B. The news item has not been created yet — first create the record
    this.isLoading = true;
    try {
      this.admpNews.id = 0;
      this.admpNews.created_by =
        (this.contactForLoggedInUser?.firstName || '') + ' ' + (this.contactForLoggedInUser?.lastName || '');

      const response = await this.actionUpdateAdmpNews(this.admpNews);
      const createdId = response?.result?.id ?? 0;

      if (createdId) {
        // Remember the ID and update the model
        this.admpNewsCreatedId = createdId;
        this.admpNews.id = createdId;
        this.admpNews = Object.assign({}, this.admpNews, response.result);

        // Update the browser's address bar WITHOUT reloading the route
        this.updateUrlSilently(createdId);

        // 3. Instantly open the document dialogue — the skeleton will no longer appear!
        this.$nextTick(() => {
          this.admpListNewsDocumentsRef?.openAddDialog();
        });
      }
    } catch (e) {
      logger.error('Error saving news before adding document:', e);
    } finally {
      this.isLoading = false;
    }
  }

  public async onImageFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > CONST.maxFileSize5MB) {
      DefaultViewHelper.showSnackbarError('Die Datei ist zu groß (max. 5MB)', 4000);
      input.value = '';
      return;
    }

    // Instant preview
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.admpNews.news_image = e.target.result;
    };
    reader.readAsDataURL(file);

    const currentId = this.admpNews.id || this.admpNewsCreatedId;
    const oldImgFile = (this.admpNews.news_files || []).find((x: any) => x.bucket_name === 'admpnl-news-images');

    const formData = new FormData();
    formData.append('File', file, file.name);
    formData.append('FileName', file.name);
    formData.append('BucketName', 'admpnl-news-images');
    formData.append('AdmpNewsId', String(currentId));
    formData.append('Id', oldImgFile?.id ? String(oldImgFile.id) : '0');
    this.isImageUploading = true;
    try {
      await this.actionUploadNewsImage(formData).then(() => {
        this.snackbarTitle = this.$te('success') ? (this.$t('success') as string) : 'Erfolgreich gespeichert';
        this.snackbarMessage = this.$te('success_uploaded')
          ? (this.$t('success_uploaded') as string)
          : 'Bild erfolgreich hochgeladen';
        this.showSuccessSnackbar = true;
      });

      if (currentId) {
        this.actionUpdateAdmpNewsImages({ id: currentId, imageLink: null });
      }
      await this.getAdmpNews(false);
    } catch (error) {
      logger.error('Failed to upload image:', error);
      this.admpNews.news_image = null;
    } finally {
      this.isImageUploading = false;
      input.value = '';
    }
  }

  public deleteImage() {
    this.isDeleteImageDialogOpen = true;
  }

  public async executeDeleteImage() {
    const oldImgFile = (this.admpNews.news_files || []).find((x: any) => x.bucket_name === 'admpnl-news-images');

    if (oldImgFile) {
      this.isDeleteImage = true;
      try {
        await this.actionDeleteAdmpNewsFile({
          FileName: oldImgFile.filename,
          BucketName: oldImgFile.bucket_name,
          admpNewsFileId: oldImgFile.id,
        });

        const idx = this.admpNews.news_files.findIndex((x: any) => x.id === oldImgFile.id);
        if (idx > -1) {
          this.admpNews.news_files.splice(idx, 1);
        }
        this.admpNews.news_image = null;
        const currentId = this.admpNews.id || this.admpNewsCreatedId;
        if (currentId) {
          this.actionUpdateAdmpNewsImages({ id: currentId, imageLink: null });
        }
        if (this.imageFileInput) {
          this.imageFileInput.value = '';
        }
      } catch (e) {
        logger.error('Failed to delete image:', e);
      } finally {
        this.isDeleteImage = false;
        this.isDeleteImageDialogOpen = false;
      }
    } else {
      this.admpNews.news_image = null;
      this.isDeleteImageDialogOpen = false;
    }
  }

  public async onDocumentUploaded(uploadedFile: any) {
    // In the background, refresh the data from the server (we pass `false` so that the skeleton doesn’t flicker)
    await this.getAdmpNews(false);
  }

  //#endregion
}
